import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Accordion,
  Button,
  Card,
  Col,
  Form,
  ListGroup,
  Row,
  Table,
} from "react-bootstrap";
import moment from "moment";
import { useCombobox } from "downshift";

import { searchSurgeryProviders } from "@services/cat-surgery-providers";
import {
  addCaseSurgeryProvider,
  getCaseSurgeryProviders,
  removeCaseSurgeryProvider,
} from "@services/case-surgery-providers";
import {
  SURGERY_PROVIDERS_NOTE,
  optionBehavior,
} from "@constants/surgery-provider-options";

const OPTION_STYLES = {
  Si: { background: "#E8FFF3", highlight: "#CFF7E3", color: "#1B7A54", accent: "#50CD89" },
  No: { background: "#FFF4DE", highlight: "#FFE7BB", color: "#8A6100", accent: "#FFA800" },
};

const normalize = (value) =>
  (value || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();

const errorMessage = (error, fallback) => {
  const data = error?.response?.data;
  return typeof data === "string" && data.trim() ? data : fallback;
};

/**
 * La tabla es el estado del acordion: las opciones de captura son proveedores fijos del
 * catalogo, asi que elegirlas deja un renglon igual que cualquier proveedor y el Si/No del
 * caso se deduce de lo registrado, no de un campo aparte que pueda contradecirlo.
 */
export const SurgeryProvidersSection = ({ disabled = true, shake = false, onShakeEnd, ...props }) => {
  const caseId = props.data?.id;
  const [rows, setRows] = useState([]);

  const exclusiveRow = rows.find((row) => optionBehavior(row.optionKey)?.requires === "No") || null;
  const requires = rows.length === 0 ? null : exclusiveRow ? "No" : "Si";

  const refreshRows = () => {
    if (!caseId) return Promise.resolve();
    return getCaseSurgeryProviders(caseId).then((r) => setRows(r.data || []));
  };

  useEffect(() => {
    if (!caseId) return;
    getCaseSurgeryProviders(caseId).then((r) => setRows(r.data || []));
  }, [caseId]);

  useEffect(() => {
    // Lo que valida la pantalla al enviar: basta un renglon, sea opcion o proveedor.
    window.surgery_providers_count = rows.length;
    window.requires_surgery_providers = requires;
  }, [rows, requires]);

  return (
    <Card className={shake ? "shake-bounce" : ""} onAnimationEnd={shake ? onShakeEnd : undefined}>
      <Card.Header>
        <Accordion.Toggle
          as={Button}
          variant="link"
          eventKey={props.eventKey}
          disabled={disabled}
        >
          Proveedores de Cirugia
        </Accordion.Toggle>
        <SurgeryProvidersStatusChip
          exclusiveRow={exclusiveRow}
          requires={requires}
          rows={rows}
        />
        <i
          className={`fa ${props.icon}`}
          style={{ position: "absolute", right: 0, marginRight: "15px", marginTop: "10px" }}
        />
      </Card.Header>
      <Accordion.Collapse eventKey={props.eventKey}>
        <Card.Body>
          <SurgeryProvidersContent
            {...props}
            caseId={caseId}
            rows={rows}
            exclusiveRow={exclusiveRow}
            requires={requires}
            onSaved={refreshRows}
          />
        </Card.Body>
      </Accordion.Collapse>
    </Card>
  );
};

const CHIP_STYLE = { fontSize: "11px", whiteSpace: "normal" };

/**
 * Indicador en el encabezado: dice lo mismo que la tabla, en corto. Cuando hay proveedores
 * de red y fuera de red a la vez, un solo chip no puede decir ambas cosas, asi que se
 * muestra uno por tipo (mismo criterio y colores que la columna RED de la tabla).
 */
const SurgeryProvidersStatusChip = ({ exclusiveRow, requires, rows }) => {
  if (!requires) return null;

  if (exclusiveRow) {
    return (
      <span className="badge badge-warning ml-2" style={CHIP_STYLE}>
        {exclusiveRow.nombreProveedor}
      </span>
    );
  }

  const networkCount = rows.filter((row) => row.isNetworkProvider !== "No").length;
  const outOfNetworkCount = rows.length - networkCount;

  return (
    <>
      {networkCount > 0 && (
        <span className="badge badge-success ml-2" style={CHIP_STYLE}>
          PROVEEDOR DE RED ({networkCount})
        </span>
      )}
      {outOfNetworkCount > 0 && (
        <span className="badge badge-warning ml-2" style={CHIP_STYLE}>
          NO USO PROVEEDOR DE RED ({outOfNetworkCount})
        </span>
      )}
    </>
  );
};

const SurgeryProvidersContent = ({
  caseId,
  rows,
  exclusiveRow,
  requires,
  onSaved,
  data,
  disabledAll,
}) => {
  const hospitalEst = data?.hospital?.hospitalEst;
  const [submitting, setSubmitting] = useState(false);
  const [manualCapture, setManualCapture] = useState(false);
  const [error, setError] = useState(null);

  // Declarar que no hubo proveeduria cierra la captura: para agregar proveedores hay que
  // quitar ese renglon de la tabla.
  useEffect(() => {
    if (exclusiveRow) setManualCapture(false);
  }, [exclusiveRow]);

  return (
    <>
      <Alert variant="dark" className="text-center" style={{ fontSize: "14px" }}>
        PROVEEDORES DE CIRUGIA
      </Alert>

      <Row className="mb-3">
        <Col>
          <i className="fa fa-question-circle h2 mr-2" />
          {SURGERY_PROVIDERS_NOTE}
        </Col>
      </Row>

      <Card>
        <Card.Body>
          <SurgeryProviderSelector
            caseId={caseId}
            hospitalEst={hospitalEst}
            disabledAll={disabledAll}
            submitting={submitting}
            setSubmitting={setSubmitting}
            rows={rows}
            exclusiveRow={exclusiveRow}
            requires={requires}
            onManualCapture={() => setManualCapture(true)}
            onError={setError}
            onSaved={onSaved}
          />

          {manualCapture && !exclusiveRow && (
            <OutOfNetworkProviderForm
              caseId={caseId}
              disabledAll={disabledAll}
              submitting={submitting}
              setSubmitting={setSubmitting}
              onError={setError}
              onSaved={onSaved}
              onDone={() => setManualCapture(false)}
            />
          )}

          {error && (
            <Alert variant="danger" className="mt-3 mb-0" style={{ fontSize: "13px" }} dismissible
              onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Table responsive striped bordered hover className="mt-4">
            <thead>
              <tr>
                <th>NOMBRE DEL PROVEEDOR</th>
                <th>RED</th>
                <th>USUARIO</th>
                <th>FECHA HORA (DD/MM/AAAA HH:MM:SS)</th>
                {!disabledAll && <th />}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>{row.nombreProveedor}</td>
                  <td>
                    <NetworkChip value={row.isNetworkProvider} optionKey={row.optionKey} />
                  </td>
                  <td>{row.username}</td>
                  <td>
                    {row.createdAt
                      ? moment(row.createdAt).format("DD/MM/YYYY hh:mm:ss A")
                      : ""}
                  </td>
                  {!disabledAll && (
                    <td className="text-center">
                      <Button
                        variant="link"
                        size="sm"
                        className="text-danger p-0"
                        title="Quitar registro"
                        disabled={submitting}
                        onClick={() => {
                          setError(null);
                          setSubmitting(true);
                          removeCaseSurgeryProvider(row.id)
                            .then(() => onSaved())
                            .catch((e) =>
                              setError(errorMessage(e, "No se pudo quitar el registro."))
                            )
                            .finally(() => setSubmitting(false));
                        }}
                      >
                        <i className="fa fa-trash" />
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={disabledAll ? 4 : 5} className="text-center text-muted">
                    SIN CAPTURA
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </>
  );
};

/** RED: SI del listado, NO alta manual, y sin marca cuando el renglon es una opcion. */
const NetworkChip = ({ value, optionKey }) => {
  if (optionKey) return <span className="text-muted">—</span>;

  const isNetwork = value !== "No";
  return (
    <span className={`badge ${isNetwork ? "badge-success" : "badge-warning"}`}>
      {isNetwork ? "SI" : "NO"}
    </span>
  );
};

/**
 * Control unico: el catalogo llega con las opciones de captura encabezando la lista, y
 * elegir cualquier entrada registra en un solo paso. NO USO PROVEEDOR DE RED es la unica
 * que no deja renglon propio: abre la captura del nombre y ahi manda el cursor.
 */
const SurgeryProviderSelector = ({
  caseId,
  hospitalEst,
  disabledAll,
  submitting,
  setSubmitting,
  rows,
  exclusiveRow,
  requires,
  onManualCapture,
  onError,
  onSaved,
}) => {
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (disabledAll) {
      setLoading(false);
      return undefined;
    }
    let active = true;
    setLoading(true);
    searchSurgeryProviders("", hospitalEst)
      .then((r) => {
        if (active) setCatalog(r.data || []);
      })
      .catch(() => {
        if (active) setCatalog([]);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [hospitalEst, disabledAll]);

  const registeredIds = rows.map((row) => row.catSurgeryProviderId);
  const normalizedQuery = normalize(query);
  const matches = (text) => !normalizedQuery || normalize(text).includes(normalizedQuery);

  const items = catalog
    .filter((entry) => !registeredIds.includes(entry.id))
    .filter((entry) => matches(entry.nombreProveedor))
    // Declarar que no hubo proveeduria solo tiene sentido con la tabla vacia.
    .filter((entry) => !(optionBehavior(entry.optionKey)?.requires === "No" && rows.length > 0))
    .map((entry) => ({ entry, behavior: optionBehavior(entry.optionKey) }));

  const {
    isOpen,
    getMenuProps,
    getInputProps,
    getComboboxProps,
    getItemProps,
    highlightedIndex,
    openMenu,
    setInputValue,
  } = useCombobox({
    items,
    itemToString: (item) => (item ? item.entry.nombreProveedor : ""),
    onInputValueChange: ({ inputValue: next }) => setQuery(next || ""),
    onSelectedItemChange: ({ selectedItem }) => {
      if (!selectedItem) return;
      setInputValue("");
      onError(null);

      if (selectedItem.behavior && !selectedItem.behavior.createsRow) {
        onManualCapture();
        return;
      }
      register(selectedItem.entry);
    },
  });

  const register = (entry) => {
    if (!caseId || submitting) return;
    setSubmitting(true);
    addCaseSurgeryProvider({
      caseId,
      catSurgeryProviderId: entry.id,
      nombreProveedor: entry.nombreProveedor,
    })
      .then(() => onSaved())
      .catch((e) => onError(errorMessage(e, "No se pudo registrar.")))
      .finally(() => setSubmitting(false));
  };

  if (disabledAll) {
    return (
      <Row>
        <Col>
          <Form.Group controlId="surgeryProviderOption">
            <Form.Label>PROVEEDOR DE CIRUGIA *</Form.Label>
            <Form.Control readOnly value={requires ? `CAPTURA REGISTRADA (${requires})` : "SIN CAPTURA"} />
          </Form.Group>
        </Col>
      </Row>
    );
  }

  if (exclusiveRow) {
    return (
      <Alert variant="warning" className="mb-0" style={{ fontSize: "13px" }}>
        EL CASO DECLARA <strong>{exclusiveRow.nombreProveedor}</strong>. QUITA ESE REGISTRO DE LA
        TABLA SI NECESITAS CAPTURAR PROVEEDORES.
      </Alert>
    );
  }

  return (
    <Row>
      <Col>
        <Form.Group controlId="surgeryProviderOption">
          <Form.Label>PROVEEDOR DE CIRUGIA *</Form.Label>
          <div {...getComboboxProps()} style={{ position: "relative" }}>
            <Form.Control
              {...getInputProps({
                placeholder: loading
                  ? "CARGANDO PROVEEDORES..."
                  : "SELECCIONA UNA OPCION O UN PROVEEDOR, O ESCRIBE PARA FILTRAR",
                disabled: submitting,
                autoComplete: "off",
                onFocus: () => openMenu(),
                onClick: () => openMenu(),
              })}
            />
            {isOpen && (
              <ListGroup
                {...getMenuProps()}
                style={{
                  position: "absolute",
                  zIndex: 10,
                  top: "100%",
                  left: 0,
                  right: 0,
                  maxHeight: 320,
                  overflowY: "auto",
                  marginTop: 2,
                }}
              >
                {items.map((item, index) =>
                  item.behavior ? (
                    <OptionItem
                      key={`option-${item.entry.id}`}
                      entry={item.entry}
                      behavior={item.behavior}
                      itemProps={getItemProps({ item, index })}
                      highlighted={highlightedIndex === index}
                    />
                  ) : (
                    <ListGroup.Item
                      key={`provider-${item.entry.id}`}
                      {...getItemProps({ item, index })}
                      active={highlightedIndex === index}
                      action
                      style={{ cursor: "pointer" }}
                    >
                      {item.entry.nombreProveedor}
                      {item.entry.especialidad ? (
                        <small className="text-muted ml-2">{item.entry.especialidad}</small>
                      ) : null}
                    </ListGroup.Item>
                  )
                )}
                {items.length === 0 && (
                  <ListGroup.Item disabled>
                    {loading ? "CARGANDO PROVEEDORES..." : "SIN COINCIDENCIAS"}
                  </ListGroup.Item>
                )}
              </ListGroup>
            )}
            {/* keep menu accessible even when closed */}
            {!isOpen && <ul {...getMenuProps()} style={{ display: "none" }} />}
          </div>
        </Form.Group>
      </Col>
    </Row>
  );
};

/** Opcion de captura dentro del desplegable, resaltada segun el estatus que implica. */
const OptionItem = ({ entry, behavior, itemProps, highlighted }) => {
  const palette = OPTION_STYLES[behavior.requires];

  return (
    <ListGroup.Item
      {...itemProps}
      style={{
        cursor: "pointer",
        backgroundColor: highlighted ? palette.highlight : palette.background,
        color: palette.color,
        fontWeight: 600,
        borderLeft: `4px solid ${palette.accent}`,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <span>{entry.nombreProveedor}</span>
      <span
        className="badge ml-2"
        style={{ backgroundColor: palette.accent, color: "#FFFFFF" }}
      >
        {behavior.requires.toUpperCase()}
      </span>
    </ListGroup.Item>
  );
};

/**
 * Captura del proveedor fuera de red: aparece con NO USO PROVEEDOR DE RED, toma el cursor y
 * registra el renglon con RED = NO. Al guardar se cierra y devuelve el control al selector,
 * porque lo normal es seguir agregando proveedores de red; si vuelve a salir uno fuera de
 * red, se elige otra vez la opcion.
 */
const OutOfNetworkProviderForm = ({
  caseId,
  disabledAll,
  submitting,
  setSubmitting,
  onError,
  onSaved,
  onDone,
}) => {
  const [nombre, setNombre] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = () => {
    const clean = nombre.trim();
    if (!clean || !caseId) return;
    onError(null);
    setSubmitting(true);
    addCaseSurgeryProvider({
      caseId,
      catSurgeryProviderId: null,
      nombreProveedor: clean,
    })
      .then(() => {
        setNombre("");
        onSaved();
        onDone();
      })
      .catch((e) => onError(errorMessage(e, "No se pudo registrar el proveedor.")))
      .finally(() => setSubmitting(false));
  };

  return (
    <Row className="align-items-end mt-2">
      <Col md={9}>
        <Form.Group controlId="surgeryProviderOutOfNetwork">
          <Form.Label>NOMBRE DEL PROVEEDOR FUERA DE RED *</Form.Label>
          <Form.Control
            ref={inputRef}
            value={nombre}
            disabled={disabledAll || submitting}
            autoComplete="off"
            placeholder="ESCRIBE EL NOMBRE DEL PROVEEDOR QUE NO PERTENECE A LA RED"
            onChange={(e) => setNombre(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSubmit();
              }
            }}
          />
        </Form.Group>
      </Col>
      <Col md={3}>
        <Form.Group>
          <Button
            variant="primary"
            type="button"
            onClick={handleSubmit}
            disabled={submitting || disabledAll || !nombre.trim()}
          >
            GUARDAR
          </Button>
          <Button
            variant="link"
            type="button"
            className="text-muted"
            onClick={() => {
              onError(null);
              onDone();
            }}
            disabled={submitting}
          >
            CANCELAR
          </Button>
        </Form.Group>
      </Col>
    </Row>
  );
};
